import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { Sparkles, ChevronRight, LogOut, Shield, Home, PenTool, Target, Trophy, Cloud, HardDrive, HelpCircle } from 'lucide-react'
import { Button } from '@/components/ui'
import { BottomNav } from './BottomNav'
import { useAuth } from '@/context/AuthContext'
import { useFamily } from '@/context/FamilyContext'
import { useStudio } from '@/context/StudioContext'
import { isAdmin } from '@/services/adminService'

interface NavItem {
  label: string
  path: string
  icon: typeof Home
}

const navItems: NavItem[] = [
  { label: 'Home', path: '/dashboard', icon: Home },
  { label: 'Write', path: '/stories', icon: PenTool },
  { label: 'Missions', path: '/dashboard', icon: Target },
  { label: 'Badges', path: '/dashboard', icon: Trophy },
]

// Avatar emoji mapping
const avatarEmojis: Record<string, string> = {
  dragon: '🐉',
  unicorn: '🦄',
  robot: '🤖',
  astronaut: '👨‍🚀',
  wizard: '🧙',
  superhero: '🦸',
  cat: '🐱',
  dog: '🐕',
}

export function AppLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, signOut } = useAuth()
  const { activeWriter, setActiveWriter } = useFamily()
  const { mode } = useStudio()
  const showAdminLink = isAdmin(user?.id)

  const handleSwitchWriter = () => {
    setActiveWriter(null)
    navigate('/select-writer')
  }

  const handleLogout = async () => {
    await signOut()
    navigate('/')
  }

  const isActive = (path: string, label: string) => {
    if (label === 'Write') {
      return location.pathname.startsWith('/stories')
    }
    if (label === 'Home') {
      return location.pathname === '/dashboard'
    }
    return location.pathname === path
  }

  const avatarEmoji = avatarEmojis[activeWriter?.avatar_id || 'dragon'] || '👤'

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)] pb-16 md:pb-0">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            {/* Left: Logo */}
            <Link to="/dashboard" className="flex items-center gap-2 sm:gap-3">
              <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-[var(--color-kids-primary)]" />
              <span className="font-display text-lg sm:text-xl font-bold text-[var(--color-kids-text)]">
                Inkwell Jr.
              </span>
            </Link>

            {/* Center: Desktop nav (hidden on mobile) */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const active = isActive(item.path, item.label)
                return (
                  <Link
                    key={item.label}
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-[var(--radius-kid)] transition-colors ${
                      active
                        ? 'bg-[var(--color-kids-primary)]/10 text-[var(--color-kids-primary)]'
                        : 'text-[var(--color-kids-text-muted)] hover:bg-[var(--color-kids-surface-alt)] hover:text-[var(--color-kids-text)]'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </Link>
                )
              })}
            </nav>

            {/* Right: User controls */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Writer selector */}
              <button
                onClick={handleSwitchWriter}
                className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-[var(--radius-kid)] hover:bg-[var(--color-kids-surface-alt)] focus:outline-none focus:ring-2 focus:ring-[var(--color-kids-primary)]/30 transition-colors"
                title={mode === 'cloud' ? 'Cloud Studio - Synced across devices' : 'Local Studio - Saved on this device'}
              >
                <span className="text-xl sm:text-2xl">{avatarEmoji}</span>
                <span className="hidden sm:inline font-semibold text-[var(--color-kids-text)] truncate max-w-[100px]">
                  {activeWriter?.display_name}
                </span>
                {mode === 'cloud' ? (
                  <Cloud className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[var(--color-kids-success)]" />
                ) : (
                  <HardDrive className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[var(--color-kids-text-muted)]" />
                )}
                <ChevronRight className="w-4 h-4 text-[var(--color-kids-text-muted)] hidden sm:block" />
              </button>

              <Link to="/help" className="hidden sm:block" data-tour="help">
                <Button variant="ghost" size="sm" title="Help & Guide">
                  <HelpCircle className="w-4 h-4" />
                </Button>
              </Link>

              {showAdminLink && (
                <Link to="/admin" className="hidden sm:block">
                  <Button variant="ghost" size="sm" title="Admin Panel">
                    <Shield className="w-4 h-4" />
                  </Button>
                </Link>
              )}

              <Button variant="ghost" size="sm" onClick={handleLogout} className="hidden sm:flex">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main>
        <Outlet />
      </main>

      {/* Bottom nav (mobile only) */}
      <BottomNav />
    </div>
  )
}
